import React, { useState, useEffect, useRef } from 'react';
import { isAudioFeedbackEnabled, setAudioFeedbackEnabled } from '../../../utils/audioFeedback';
import { CameraCapture } from '../auth/CameraCapture';
import { AvatarCropPreview } from './AvatarCropPreview';
import './ProfileSheet.css';

export interface ProfileSheetProps {
  isOpen: boolean;
  onClose: () => void;
  user: { name: string; avatarUrl?: string };
  progress?: { completed: number; total: number };
  onLogout: () => void;
  onAvatarChange?: (blob: Blob | null) => void;
}

export const ProfileSheet: React.FC<ProfileSheetProps> = ({
  isOpen,
  onClose,
  user,
  progress,
  onLogout,
  onAvatarChange,
}) => {
  const [reminders, setReminders] = useState(true);
  const [audioFb, setAudioFb] = useState(isAudioFeedbackEnabled);
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  // Avatar edit flow
  const [showAvatarMenu, setShowAvatarMenu] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const openPreviewFromBlob = (blob: Blob) => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(blob));
  };

  const handleGalleryFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) openPreviewFromBlob(file);
    e.target.value = '';
  };

  const handleCameraCapture = (blob: Blob) => {
    setShowCamera(false);
    openPreviewFromBlob(blob);
  };

  const handleAcceptCrop = (cropped: Blob) => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    onAvatarChange?.(cropped);
  };

  const handleRetakeCrop = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setShowAvatarMenu(true);
  };

  const handleRemoveAvatar = () => {
    setShowAvatarMenu(false);
    onAvatarChange?.(null);
  };

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      setClosing(false);
    } else if (visible) {
      setClosing(true);
      const t = setTimeout(() => { setVisible(false); setClosing(false); }, 280);
      return () => clearTimeout(t);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!visible) return null;

  const handleClose = () => {
    setClosing(true);
    setTimeout(onClose, 280);
  };

  const progressPct = progress
    ? Math.round((progress.completed / progress.total) * 100)
    : 0;

  return (
    <>
      <div className={`ps-overlay${closing ? ' ps-closing' : ''}`} onClick={handleClose} />
      <div className={`ps-sheet${closing ? ' ps-closing' : ''}`} onClick={e => e.stopPropagation()}>
        <div className="ps-handle"><div className="ps-handle-bar" /></div>

        {/* Avatar + Name */}
        <div className="ps-user">
          <button
            type="button"
            className="ps-avatar ps-avatar-button"
            onClick={() => onAvatarChange && setShowAvatarMenu(true)}
            aria-label={user.avatarUrl ? 'Change profile photo' : 'Add profile photo'}
            disabled={!onAvatarChange}
          >
            <span className="ps-avatar-clip">
              {user.avatarUrl ? (
                <img src={user.avatarUrl} alt={user.name} className="ps-avatar-img" />
              ) : (
                <span className="ps-avatar-initial">{user.name.charAt(0).toUpperCase()}</span>
              )}
            </span>
            {onAvatarChange && (
              <span className="ps-avatar-edit" aria-hidden="true">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
              </span>
            )}
          </button>
          <div className="ps-user-info">
            <div className="ps-user-name">{user.name}</div>
            <div className="ps-user-role">Participant</div>
          </div>
        </div>

        {/* Menu rows */}
        <div className="ps-rows">
          {/* Task Reminders */}
          <div className="ps-row">
            <div className="ps-row-left">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="1.5" strokeLinecap="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              <span>Task Reminders</span>
            </div>
            <button
              className={`ps-toggle ${reminders ? 'ps-toggle-on' : ''}`}
              onClick={() => setReminders(r => !r)}
              aria-label="Toggle task reminders"
            >
              <div className="ps-toggle-thumb" />
            </button>
          </div>

          {/* Audio Feedback */}
          <div className="ps-row">
            <div className="ps-row-left">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="1.5" strokeLinecap="round">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
              </svg>
              <span>Sound Effects</span>
            </div>
            <button
              className={`ps-toggle ${audioFb ? 'ps-toggle-on' : ''}`}
              onClick={() => {
                const next = !audioFb;
                setAudioFb(next);
                setAudioFeedbackEnabled(next);
              }}
              aria-label="Toggle sound effects"
            >
              <div className="ps-toggle-thumb" />
            </button>
          </div>

          {/* Study Progress */}
          {progress && (
            <div className="ps-row ps-row-progress">
              <div className="ps-row-left">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="1.5" strokeLinecap="round">
                  <polyline points="9,11 12,14 22,4" />
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                </svg>
                <span>Study Progress</span>
              </div>
              <span className="ps-progress-label">{progress.completed}/{progress.total}</span>
            </div>
          )}

          {progress && (
            <div className="ps-progress-bar-wrap">
              <div className="ps-progress-bar" style={{ width: `${progressPct}%` }} />
            </div>
          )}

          {/* Privacy & Data */}
          <div className="ps-row ps-row-tap">
            <div className="ps-row-left">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="1.5" strokeLinecap="round">
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <span>Privacy &amp; Data</span>
            </div>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2" strokeLinecap="round">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </div>
        </div>

        {/* Log Out */}
        <button className="ps-logout" onClick={onLogout}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          Log Out
        </button>
      </div>

      {/* Hidden gallery input */}
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        onChange={handleGalleryFile}
        style={{ display: 'none' }}
      />

      {/* Avatar action sheet */}
      {showAvatarMenu && (
        <>
          <div className="ps-avatar-menu-backdrop" onClick={() => setShowAvatarMenu(false)} />
          <div className="ps-avatar-menu" onClick={(e) => e.stopPropagation()}>
            <button
              className="ps-avatar-menu-item"
              onClick={() => { setShowAvatarMenu(false); setShowCamera(true); }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
                <circle cx="12" cy="13" r="4" />
              </svg>
              Take Photo
            </button>
            <button
              className="ps-avatar-menu-item"
              onClick={() => { galleryInputRef.current?.click(); setShowAvatarMenu(false); }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21,15 16,10 5,21" />
              </svg>
              Choose from Gallery
            </button>
            {user.avatarUrl && (
              <button
                className="ps-avatar-menu-item ps-avatar-menu-item-danger"
                onClick={handleRemoveAvatar}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <polyline points="3,6 5,6 21,6" />
                  <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                </svg>
                Remove Photo
              </button>
            )}
          </div>
        </>
      )}

      {/* Camera modal */}
      <CameraCapture
        isOpen={showCamera}
        onCapture={handleCameraCapture}
        onClose={() => setShowCamera(false)}
      />

      {/* Crop preview */}
      {previewUrl && (
        <AvatarCropPreview
          sourceUrl={previewUrl}
          onAccept={handleAcceptCrop}
          onRetake={handleRetakeCrop}
        />
      )}
    </>
  );
};
